import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bot, X, MessageCircle, Zap, HelpCircle, Settings } from 'lucide-react';

const quickActions = [
  { icon: Zap, label: "Generate Dockerfile" },
  { icon: HelpCircle, label: "Kubernetes Help" },
  { icon: Settings, label: "Deployment Config" }
];

const FloatingAssistant: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) return;
    setMessage('');
  };

  return (
    <>
      {/* Floating Button */}
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 2, type: "spring", stiffness: 260, damping: 20 }}
        className="fixed bottom-6 right-6 z-40"
      >
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={() => setIsOpen(!isOpen)}
          aria-label={isOpen ? "Close assistant" : "Open assistant"}
          aria-expanded={isOpen}
          className="w-16 h-16 bg-gradient-to-br from-primary-500 to-neon-blue rounded-full flex items-center justify-center shadow-2xl neon-glow hover:neon-glow transition-all duration-300"
        >
          <Bot className="w-8 h-8 text-white" />
        </motion.button>
      </motion.div>

      {/* Assistant Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 20 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="fixed bottom-24 right-6 z-40 w-80"
          >
            <div className="glass rounded-2xl shadow-2xl border border-white/10 overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-r from-primary-600 to-neon-purple p-4 flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                    <Bot className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold">DeploAI Assistant</h3>
                    <p className="text-white/80 text-sm">AI-powered DevOps help</p>
                  </div>
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  aria-label="Dismiss assistant panel"
                  className="text-white/80 hover:text-white transition-colors duration-200"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Content */}
              <div className="p-4">
                {/* Welcome Message */}
                <div className="mb-6">
                  <p className="text-gray-300 text-sm leading-relaxed">
                    👋 Hi! The DeploAI assistant isn't available yet, but here's what it will help with:
                  </p>
                </div>

                {/* Quick Actions (preview only, not yet functional) */}
                <div className="space-y-3 mb-6">
                  {quickActions.map((action) => (
                    <div
                      key={action.label}
                      className="w-full glass rounded-lg p-3 flex items-center space-x-3 opacity-60"
                    >
                      <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-neon-blue rounded-lg flex items-center justify-center">
                        <action.icon className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-gray-300">{action.label}</span>
                      <span className="text-gray-500 text-xs ml-auto">Coming soon</span>
                    </div>
                  ))}
                </div>

                {/* Chat Input */}
                <form onSubmit={handleSubmit} className="flex space-x-2">
                  <label htmlFor="assistant-message" className="sr-only">
                    Message the assistant
                  </label>
                  <input
                    id="assistant-message"
                    type="text"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Ask me anything..."
                    className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-gray-300 placeholder-gray-500 focus:outline-none focus:border-neon-blue transition-colors duration-200"
                  />
                  <button
                    type="submit"
                    disabled={!message.trim()}
                    aria-label="Send message"
                    className="w-10 h-10 bg-gradient-to-br from-primary-500 to-neon-blue rounded-lg flex items-center justify-center hover:scale-105 transition-transform duration-200 disabled:opacity-40 disabled:hover:scale-100"
                  >
                    <MessageCircle className="w-5 h-5 text-white" />
                  </button>
                </form>

                {/* Status */}
                <div className="mt-4 flex items-center space-x-2">
                  <div className="w-2 h-2 bg-amber-400 rounded-full"></div>
                  <span className="text-gray-400 text-xs">Assistant coming soon</span>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>


    </>
  );
};

export default FloatingAssistant; 